import React, { useEffect, useState } from 'react';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { Field } from 'formik';
import PropTypes from 'prop-types';
import { get, isEmpty } from 'lodash';
import { Box, TextField } from '@mui/material';
import { it, enUS } from 'date-fns/locale';
import { makeStyles } from '@mui/styles';

import { requirements, validateField } from './validation';
import {
  isValidDate,
  formatDateForm,
  formatFristDayToStandard,
} from '@utils/formatIfDate';
import i18n from '../../i18n';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(() => ({
  root: {
    '& button': {
      padding: 0,
    },
  },
  label: {
    width: '70%',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
  },
  labelShrink: {
    width: '100%',
  },
}));

const locale = (language) => {
  switch (language) {
    case 'it':
      return it;
    case 'en':
      return enUS;
    default:
      return it;
  }
};

const FormikKeyboardDatePicker = React.memo(
  ({ name, label, error, value, setFieldValue, ...otherProps }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [changed, setChanged] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);

    const handleDateChange = (date) => {
      setChanged(true);
      setSelectedDate(date);
      const formattedDate = formatDateForm(date);
      setFieldValue(name, formattedDate);
    };

    useEffect(() => {
      if (!isEmpty(value) && !changed) {
        const formattedDate = formatFristDayToStandard(value);
        if (formattedDate && isValidDate(formattedDate)) {
          setSelectedDate(formattedDate);
        }
      }
    }, [value, changed]);

    return (
      <Box position="relative">
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={locale(i18n.language)}>
          <Field
            name={name}
            validate={validateField(name)}
            render={({ field, form: { ...props } }) => (
              <DatePicker
                {...field}
                {...props}
                label={label}
                inputFormat="dd/MM/yyyy"
                value={selectedDate}
                onChange={handleDateChange}
                minDate={get(requirements, `${name}.minDate`)}
                maxDate={get(requirements, `${name}.maxDate`)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    size="small"
                    error={error || params.error}
                    helperText={
                      params.error ? t('invalid_date') : params.helperText
                    }
                    id={`${name}-${label}-datepicker`}
                    className={classes.root}
                    InputLabelProps={{
                      ...params.InputLabelProps,
                      classes: {
                        root: classes.label,
                        shrink: classes.labelShrink,
                      },
                    }}
                  />
                )}
                {...otherProps}
              />
            )}
          />
        </LocalizationProvider>
      </Box>
    );
  },
);

FormikKeyboardDatePicker.propTypes = {
  error: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  setFieldValue: PropTypes.func.isRequired,
};

FormikKeyboardDatePicker.defaultProps = {
  value: '',
  label: '',
};

export default FormikKeyboardDatePicker;
